import { useCallback, useEffect, useId, useState } from "react";
import { useSearchParams } from "react-router-dom";

import NotFoundPage from "components/error/NotFoundPage";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import { getSearchQuery } from "components/SearchInput/helpers";
import useErrorHandle from "hooks/useErrorHandle";
import useURLParams from "hooks/useURLParams";
import TablePagination from "components/Table/Pagination";
import usePagination from "components/Table/Pagination/usePagination";
import { PAGINATION_OPTIONS } from "components/Table/Pagination/constants";

import { TEMPLATES_LIMIT, TEMPLATES_LIMIT_STORAGE_KEY } from "./constants";
import { showCreateVersionDrawer } from "./CreateVersionDrawer";
import TemplateVersionsEmpty from "./Empty";
import TemplatesFeatureGateCallout from "../../TemplatesWorkbench/FeatureGate/Callout";
import FiltersLayout from "./FiltersLayout";
import TemplateVersionsPageLayout from "./PageLayout";
import useTemplates from "./hooks/useTemplates";
import TemplatesTable from "./Table";

type TemplateVersionsProps = {
  id: string;
};

const TemplateVersions = ({ id }: TemplateVersionsProps) => {
  const urlParams = useURLParams();
  const searchInput = getSearchQuery(urlParams);
  const [itemsCount, setItemsCount] = useState<number>(0);
  const [searchParams] = useSearchParams();

  const {
    initialLoading,
    error,
    templates,
    loadTemplates,
    endCursor,
    startCursor,
    noResults,
    isEmpty,
    loadingContent,
    predicates,
  } = useTemplates({ id });

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
    resetToInitialPage: resetPaginationToInitialPage,
  } = usePagination({
    fetchItems: loadTemplates,
    endCursor,
    startCursor,
    visibleItemsCount: templates.length,
    initialLimit: TEMPLATES_LIMIT,
    allItemsCount: itemsCount,
    storageKey: TEMPLATES_LIMIT_STORAGE_KEY,
  });

  const handleSortCallback = useCallback(() => {
    resetPaginationToInitialPage();
  }, [resetPaginationToInitialPage]);

  useEffect(() => {
    if (searchParams.size === 0 && page !== 1) {
      goToFirstPage();
    }
  }, [searchParams, page, goToFirstPage]);

  const handleOpenCreateVersionDrawer = () => {
    showCreateVersionDrawer({ templateId: id });
  };

  const ErrorContent = useErrorHandle(error);

  const titleId = useId();

  if (ErrorContent) {
    return ErrorContent;
  }

  if (initialLoading) {
    return (
      <TemplateVersionsPageLayout titleId={titleId}>
        <PageLayoutSkeleton />
      </TemplateVersionsPageLayout>
    );
  }

  if (noResults) {
    return <NotFoundPage />;
  }

  const hasNoResultsWithFiltering = searchInput.length > 0 || predicates.length > 0;

  return (
    <TemplateVersionsPageLayout titleId={titleId} onCreate={handleOpenCreateVersionDrawer}>
      <TemplatesFeatureGateCallout />
      <FiltersLayout
        predicates={predicates}
        itemsCount={itemsCount}
        setItemsCount={setItemsCount}
        titleId={titleId}
        id={id}
      >
        {isEmpty && (
          <TemplateVersionsEmpty
            hasNoResults={hasNoResultsWithFiltering}
            onCreate={handleOpenCreateVersionDrawer}
          />
        )}

        {templates.length > 0 && (
          <>
            <TemplatesTable
              templates={templates}
              loading={loadingContent}
              rowsPerPage={rowsPerPage}
              sortCallback={handleSortCallback}
            />

            <TablePagination
              from={from}
              to={to}
              loading={loadingContent}
              goToNextPage={goToNextPage}
              setRowsPerPage={setRowsPerPage}
              goToPrevPage={goToPrevPage}
              goToFirstPage={goToFirstPage}
              goToLastPage={goToLastPage}
              rowsPerPage={rowsPerPage}
              itemsCount={itemsCount}
              lastPage={lastPage}
              page={page}
              paginationOptions={PAGINATION_OPTIONS}
            />
          </>
        )}
      </FiltersLayout>
    </TemplateVersionsPageLayout>
  );
};

export default TemplateVersions;
